import { instanceOfErrorListResponse, Service, type ErrorListResponse } from '@genial/services';
import { useRoute, type RouteProp } from '@react-navigation/native';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { usePaginatedState } from '../../../hooks/usePaginatedState';
import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../hooks/useToastCreator';
import type { SignedInRootStackParamList } from '../../../routes/types';
import { normalizeBatchTicketRedeemRows } from '../../../store/batchTicket/normalize';
import { batchTicketRedeemRowsQuery } from '../../../store/batchTicket/selectors/redeem';
import { useErrorToast } from '../hooks/toast-errpr';
import { screenName, errorExportRedeemSpreadsheetSentMessage } from '../utils/constants';

interface RedeemRowsContextType {
  redeemRowsData: ReturnType<typeof normalizeBatchTicketRedeemRows>;
  isLoadingRedeemRows: boolean;
  isLoadingMoreRedeemRows: boolean;
  searchTermRedeemRows: string;
  handleSearchRedeemRows: (term: string) => void;
  handleClearSearchRedeemRows: () => void;
  handleRemoveRedeemRowItem: (basketCustomerOfferId: string, fileRowId: string) => Promise<void>;
  onLoadingMoreRedeemRows: () => Promise<void>;
  redeemRowsRefresher: () => void;
  redeemRowsIsError: boolean;
}

const RedeemRowsContext = createContext<RedeemRowsContextType>({} as RedeemRowsContextType);

export type RedeemItemProps = ReturnType<typeof normalizeBatchTicketRedeemRows>[number];

export const RedeemRowsProvider = ({ children }: { children: React.ReactNode }) => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'RedeemBatchTicketDetails'>>();
  const { handleErrorToast } = useErrorToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTermRedeemRows, setSearchTermRedeemRows] = useState('');
  const isFirstPage = currentPage === 1;

  const {
    data: redeemRowsResponse,
    error: redeemRowsIsError,
    loading: isLoadingRedeemRows,
    refresher: redeemRowsRefresher,
  } = useRecoilQuery(
    batchTicketRedeemRowsQuery({
      basketAdvisorId: params.basketAdvisorId,
      page: currentPage,
      searchTerm: searchTermRedeemRows,
    }),
    { cachePolicy: 'no-cache' }
  );

  const [redeemRowsData, setRedeemRowsData] = usePaginatedState(
    redeemRowsResponse?.data,
    currentPage
  );

  const { showToast, showToastFromError } = useToastCreator('toast-redeem-offers-ticket');

  const onLoadingMoreRedeemRows = async () => {
    if (!redeemRowsResponse?.meta?.pagination.hasNext || isLoadingRedeemRows || redeemRowsIsError) {
      return;
    }

    setCurrentPage((prev) => prev + 1);
  };

  const handleRemoveRedeemRowItem = async (basketCustomerOfferId: string, fileRowId: string) => {
    try {
      await Service.Basket.V1.deleteCustomerOfferByCustomerOfferIdFileRowId({
        customerOfferId: basketCustomerOfferId,
        screenName,
        fileRowId,
      });

      setRedeemRowsData((state) => state.filter((item) => item.id !== fileRowId));
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorExportRedeemSpreadsheetSentMessage);
    }
  };

  const handleSearchRedeemRows = (term: string) => {
    setSearchTermRedeemRows(term);
    setRedeemRowsData([]);
    setCurrentPage(1);
  };

  const handleClearSearchRedeemRows = () => {
    setSearchTermRedeemRows('');
    setCurrentPage(1);
  };

  useEffect(() => {
    handleErrorToast(redeemRowsIsError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redeemRowsIsError]);

  return (
    <RedeemRowsContext.Provider
      value={{
        redeemRowsData,
        isLoadingRedeemRows: isLoadingRedeemRows && isFirstPage,
        isLoadingMoreRedeemRows: isLoadingRedeemRows && !isFirstPage,
        searchTermRedeemRows,
        handleSearchRedeemRows,
        handleClearSearchRedeemRows,
        handleRemoveRedeemRowItem,
        onLoadingMoreRedeemRows,
        redeemRowsRefresher,
        redeemRowsIsError: !!redeemRowsIsError,
      }}>
      {children}
    </RedeemRowsContext.Provider>
  );
};

export const useRedeemRowsContext = (): RedeemRowsContextType => {
  const context = useContext(RedeemRowsContext);

  if (!context) {
    throw new Error('useRedeemRowsContext must be used within an RedeemRowsContext');
  }
  return context;
};
